/*
 * Extra Distressing add-on.
 *
 * Adds an optional, paid add-on ("Extra Distressing", +$10 AUD) to eligible
 * hat products. Mirrors under-brim-message.js: the $10 charge is a real,
 * separate line item (a hidden "add-on" product) kept in lock-step with the
 * hat's line item via a shared, randomly generated `_distressing_addon_id`
 * line item property. The chosen distressing level (Light / Medium / Strong)
 * is submitted as an ordinary line item property on the hat's own
 * <product-form>, so the existing Add to cart flow (Dawn's product-form.js)
 * is left completely untouched -- this script only adds a second, linked
 * line item after a successful add, and keeps quantities/removals in sync
 * afterwards.
 *
 * NOTE: this page may also load under-brim-message.js, which follows the
 * exact same pattern for a different add-on. Both scripts add their addon
 * line item via /cart/add.js in response to the *same* product-form submit.
 * Two concurrent writes to the same Shopify cart session can race (a
 * classic lost-update: both requests read the cart before either write
 * lands, so the second write can silently clobber the first, dropping one
 * of the two addons). window.__dvCartMutationQueue below serializes every
 * cart-mutating request from *either* script so they never race.
 */
(() => {
  if (window.ExtraDistressing) return;

  window.ExtraDistressing =
    typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
      ? { subscribe, PUB_SUB_EVENTS }
      : { subscribe: () => () => {}, PUB_SUB_EVENTS: { cartUpdate: 'cart-update' } };

  const ADDON_HANDLE = 'extra-distressing-addon';

  function generateId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return `xd_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }

  function fetchConfigJson(body) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    };
  }

  /* ------------------------------------------------------------------- *
   * Shared cart-mutation queue (serializes writes across this script AND *
   * under-brim-message.js, so their /cart/add.js and /cart/change.js     *
   * calls never race each other on the same cart session).               *
   * ------------------------------------------------------------------- */

  function queueCartMutation(task) {
    const gate = window.__dvCartMutationQueue || Promise.resolve();
    const result = gate.catch(() => {}).then(task);
    window.__dvCartMutationQueue = result.catch(() => {});
    return result;
  }

  /* ------------------------------------------------------------------- *
   * Cart section refresh helpers                                         *
   * ------------------------------------------------------------------- */

  function collectSectionIds() {
    const ids = {};
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.dataset.id) {
      ids.cartDrawer = cartDrawer.dataset.id;
    }
    const mainItems = document.getElementById('main-cart-items');
    if (mainItems && mainItems.dataset.id) {
      ids.mainCartItems = mainItems.dataset.id;
    }
    const mainFooter = document.getElementById('main-cart-footer');
    if (mainFooter && mainFooter.dataset.id) {
      ids.mainCartFooter = mainFooter.dataset.id;
    }
    if (!ids.cartIconBubble && document.getElementById('cart-icon-bubble')) {
      ids.cartIconBubble = 'cart-icon-bubble';
    }
    return ids;
  }

  function parseSection(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function applyCartResponse(sectionIds, sections) {
    if (!sections) return;

    const drawer = document.querySelector('cart-drawer');
    if (drawer && sectionIds.cartDrawer && sections[sectionIds.cartDrawer] && typeof drawer.renderContents === 'function') {
      drawer.renderContents({ sections });
    } else if (sectionIds.cartIconBubble && sections[sectionIds.cartIconBubble]) {
      const bubbleHtml = sections[sectionIds.cartIconBubble];
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const doc = parseSection(bubbleHtml);
        const fresh = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
        if (fresh) bubble.innerHTML = fresh.innerHTML;
      }
    }

    if (sectionIds.mainCartItems && sections[sectionIds.mainCartItems]) {
      const doc = parseSection(sections[sectionIds.mainCartItems]);
      const freshItems = doc.getElementById('main-cart-items');
      const oldItems = document.getElementById('main-cart-items');
      if (freshItems && oldItems) oldItems.replaceWith(freshItems);
    }
    if (sectionIds.mainCartFooter && sections[sectionIds.mainCartFooter]) {
      const doc = parseSection(sections[sectionIds.mainCartFooter]);
      const freshFooter = doc.getElementById('main-cart-footer');
      const oldFooter = document.getElementById('main-cart-footer');
      if (freshFooter && oldFooter) oldFooter.replaceWith(freshFooter);
    }
  }

  async function refreshCartUI() {
    const sectionIds = collectSectionIds();
    const values = Object.values(sectionIds);
    if (!values.length) return;
    try {
      const response = await fetch(`${window.location.pathname}?sections=${values.join(',')}`);
      if (!response.ok) return;
      const sections = await response.json();
      applyCartResponse(sectionIds, sections);
    } catch (error) {
      console.error('[extra-distressing] failed to refresh cart UI', error);
    }
  }

  /* ------------------------------------------------------------------- *
   * Cart line reconciliation (add / quantity sync / removal)             *
   * ------------------------------------------------------------------- */

  let reconciling = false;
  let reconcileAgain = false;

  async function reconcileAddonLines() {
    if (reconciling) {
      reconcileAgain = true;
      return;
    }
    reconciling = true;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

      const hatLines = cart.items.filter(
        (item) => item.handle !== ADDON_HANDLE && item.properties && item.properties._distressing_addon_id
      );
      const addonLines = cart.items.filter((item) => item.handle === ADDON_HANDLE);

      let changed = false;

      for (const hatLine of hatLines) {
        const addonId = hatLine.properties._distressing_addon_id;
        const match = addonLines.find((line) => line.properties && line.properties._distressing_addon_id === addonId);
        if (!match) {
          const addonVariantId = await getAddonVariantId();
          if (!addonVariantId) continue;
          await queueCartMutation(() =>
            fetch(
              '/cart/add.js',
              fetchConfigJson({
                id: addonVariantId,
                quantity: hatLine.quantity,
                properties: { _distressing_addon_id: addonId },
              })
            )
          );
          changed = true;
        } else if (match.quantity !== hatLine.quantity) {
          await queueCartMutation(() =>
            fetch('/cart/change.js', fetchConfigJson({ id: match.key, quantity: hatLine.quantity }))
          );
          changed = true;
        }
      }

      for (const addonLine of addonLines) {
        const addonId = addonLine.properties && addonLine.properties._distressing_addon_id;
        const hasHat = hatLines.some((line) => line.properties._distressing_addon_id === addonId);
        if (!hasHat) {
          await queueCartMutation(() =>
            fetch('/cart/change.js', fetchConfigJson({ id: addonLine.key, quantity: 0 }))
          );
          changed = true;
        }
      }

      if (changed) await refreshCartUI();
    } catch (error) {
      console.error('[extra-distressing] reconciliation failed', error);
    } finally {
      reconciling = false;
      if (reconcileAgain) {
        reconcileAgain = false;
        reconcileAddonLines();
      }
    }
  }

  let reconcileTimer;
  function scheduleReconcile(delay = 250) {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcileAddonLines, delay);
  }

  let addonVariantIdPromise = null;
  function getAddonVariantId() {
    const el = document.querySelector('extra-distressing[data-addon-variant-id]');
    if (el && el.dataset.addonVariantId) return Promise.resolve(el.dataset.addonVariantId);
    if (!addonVariantIdPromise) {
      addonVariantIdPromise = fetch(`/products/${ADDON_HANDLE}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((product) => (product && product.variants && product.variants[0] ? product.variants[0].id : null))
        .catch(() => null);
    }
    return addonVariantIdPromise;
  }

  /* ------------------------------------------------------------------- *
   * <extra-distressing> -- the product page control                      *
   * ------------------------------------------------------------------- */

  class ExtraDistressing extends HTMLElement {
    constructor() {
      super();
      this.checkbox = this.querySelector('[data-distressing-toggle]');
      this.radios = Array.from(this.querySelectorAll('[data-distressing-radio]'));
      this.addonIdInput = this.querySelector('[data-distressing-addon-id]');
      this.form = this.closest('form') || document.getElementById(this.dataset.formId);
      this.submitButton = this.form ? this.form.querySelector('[type="submit"]') : null;

      this.pendingSubmission = null;

      if (this.checkbox) {
        this.checkbox.disabled = false;
        this.checkbox.addEventListener('change', this.onToggle.bind(this));
      }
      this.radios.forEach((radio) => radio.addEventListener('change', this.updateSubmitState.bind(this)));
      if (this.form) {
        this.form.addEventListener('submit', this.onFormSubmit.bind(this));
      }

      this.updateSubmitState();
    }

    connectedCallback() {
      this.cartUpdateUnsubscriber = window.ExtraDistressing.subscribe(
        window.ExtraDistressing.PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          if (event.source !== 'product-form') return;
          this.handleCartUpdate(event);
        }
      );
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    }

    onToggle() {
      const checked = this.checkbox.checked;
      this.radios.forEach((radio) => {
        radio.disabled = !checked;
      });
      if (this.addonIdInput) this.addonIdInput.disabled = !checked;

      if (checked) {
        if (this.addonIdInput) this.addonIdInput.value = generateId();
      } else if (this.addonIdInput) {
        this.addonIdInput.value = '';
      }
      this.updateSubmitState();
    }

    isValid() {
      if (!this.checkbox || !this.checkbox.checked) return true;
      return this.radios.some((radio) => radio.checked);
    }

    updateSubmitState() {
      if (!this.submitButton) return;
      const shouldBlock = this.checkbox && this.checkbox.checked && !this.isValid();
      if (shouldBlock) {
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.dataset.extraDistressingBlocked = 'true';
        this.submitButton.classList.add('under-brim-message--blocked');
      } else if (this.submitButton.dataset.extraDistressingBlocked === 'true') {
        this.submitButton.removeAttribute('aria-disabled');
        delete this.submitButton.dataset.extraDistressingBlocked;
        this.submitButton.classList.remove('under-brim-message--blocked');
      }
    }

    onFormSubmit() {
      if (!this.checkbox) return;
      const quantityInput = this.form.querySelector('input[name="quantity"]');
      this.pendingSubmission = this.checkbox.checked
        ? {
            addonId: this.addonIdInput ? this.addonIdInput.value : null,
            quantity: quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1,
          }
        : null;
    }

    async handleCartUpdate() {
      const pending = this.pendingSubmission;
      this.pendingSubmission = null;
      if (!pending || !pending.addonId) return;

      const addonVariantId = this.dataset.addonVariantId;
      if (!addonVariantId) return;

      try {
        const sectionIds = collectSectionIds();
        const response = await queueCartMutation(() =>
          fetch(
            '/cart/add.js',
            fetchConfigJson({
              id: addonVariantId,
              quantity: pending.quantity,
              properties: { _distressing_addon_id: pending.addonId },
              sections: Object.values(sectionIds),
              sections_url: window.location.pathname,
            })
          ).then((r) => r.json())
        );

        if (response && response.sections) {
          applyCartResponse(sectionIds, response.sections);
        } else {
          await refreshCartUI();
        }
      } catch (error) {
        console.error('[extra-distressing] failed to add customization', error);
      } finally {
        if (this.checkbox) this.checkbox.checked = false;
        this.radios.forEach((radio) => {
          radio.checked = radio.defaultChecked;
          radio.disabled = true;
        });
        if (this.addonIdInput) {
          this.addonIdInput.value = '';
          this.addonIdInput.disabled = true;
        }
        this.updateSubmitState();
      }
    }
  }

  if (!customElements.get('extra-distressing')) {
    customElements.define('extra-distressing', ExtraDistressing);
  }

  /* ------------------------------------------------------------------- *
   * Sitewide cart reconciliation                                         *
   * ------------------------------------------------------------------- */

  window.ExtraDistressing.subscribe(window.ExtraDistressing.PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event && (event.source === 'product-form' || event.source === 'extra-distressing-reconcile')) return;
    scheduleReconcile();
  });
  scheduleReconcile(600);
})();
